/**
 * face-maker/app.js — in-browser 3D FACE CONSTRUCTOR built on an open parametric
 * head model (Apache-2.0; см. public/vendor/gnm/LICENSE.md). A truncated +
 * int16-quantized linear basis (identity + expression) is evaluated in JS and
 * rendered with three.js:
 *
 *   vertices = template + Σ id_k · id_basis_k + Σ expr_k · expr_basis_k   (bind pose)
 *
 * v2: preset-faces gallery with live 3D thumbnails · skin-tone / eye-color palette +
 * pupil-size slider (per-vertex REGION ids ship in the asset) · share-link (face code
 * in the URL hash) · GLB / OBJ mesh export · turntable video (WebCodecs via
 * CanvasVideoExport, MediaRecorder fallback) · streamed download progress · face
 * persists across reloads.
 *
 * v3: optional HAIR module (face-maker/hair.js, assets vendor/gnm/hair/) plugs in
 * through registerTab/recompute hooks — see hair.js header for the isolation
 * contract. Share-code ver 3 appends [hairStyle+1][hairColor]; bald faces still
 * encode as ver 2 for pre-hair clients. GLB/OBJ exports append the hair mesh.
 *
 * Assets (public/vendor/gnm/, ~8.7 MB): manifest + 6 binaries, multi-base loader —
 * intl pages fetch from the RU Beget origin first (ACAO'd, saves NL egress), page
 * origin as fallback. Every asset URL carries ?v= (immutable-cache regen trap).
 *
 * Deps (blade order): THREE r147 global, THREE.OrbitControls, CanvasVideoExport,
 * window.__downloadFile.
 */
(function () {
    'use strict';
    var SLUG = 'face-maker';
    var BASES = window.__FM_BASES || (window.__FM_BASE ? [window.__FM_BASE] : ['/vendor/gnm/']);
    var ASSET_V = window.__FM_V ? ('?v=' + window.__FM_V) : '';
    var activeBase = null;
    if (typeof THREE === 'undefined') { console.error('[face-maker] THREE not loaded'); return; }

    var $ = function (id) { return document.getElementById(id); };
    function ae(e, d) { try { if (window.__ae) window.__ae(e, d || {}); } catch (_) {} }
    function toast(m, t) { try { (window.__toast || function () {})(m, t || 'info'); } catch (_) {} }
    function L(k, fb) { try { var d = window.__FM_I18N || {}; return d[k] || fb; } catch (_) { return fb; } }

    var els = {}, renderer, scene, camera, controls, mesh, geo, disposed = false, tickId = 0;
    var V = 0, T = 0, K_ID = 0, K_EX = 0;
    var template = null, idF = null, exF = null, colors = null, regions = null, tris = null;
    var idC = null, exC = null, pos = null;
    var pose = { rx: 0, ry: 0, rz: 0 }, camZoom = 1;
    var exGroups = [], pupilIdx = -1;      // expression index of the pupil-dilation comp
    var exporting = false;
    // optional-module plumbing (hair.js): extra tabs + post-recompute hooks +
    // hair share/persist state carried even while the module hasn't booted yet
    var recomputeHooks = [], extraTabs = [], pendingHair = null;

    /* ───────── palette (skin / iris) — colors derived from per-vertex regions ─────────
       region ids: 0 skin, 1 lips, 2 gums, 3 teeth, 4 tongue, 5 sclera, 6 iris, 7 pupil.
       Lips derive from the skin tone so every tone looks coherent. */
    var SKIN_TONES = ['#f2d7c4', '#e8c3a8', '#d1a284', '#b97f58', '#8d5a3b', '#5d3a26'];
    var EYE_COLORS = ['#57351e', '#8b6a3f', '#4a7440', '#4f7fa8', '#7d8a94', '#2c1e14'];
    var palette = { skin: 2, eye: 0 };
    function hex2rgb(h) { var n = parseInt(h.slice(1), 16); return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255]; }
    var FIXED = { 2: [0.86, 0.47, 0.50], 3: [0.93, 0.91, 0.86], 4: [0.80, 0.40, 0.42], 5: [0.94, 0.93, 0.92], 7: [0.04, 0.04, 0.05] };
    function computeColors() {
        if (!regions || !colors) return;
        var skin = hex2rgb(SKIN_TONES[palette.skin] || SKIN_TONES[2]);
        var iris = hex2rgb(EYE_COLORS[palette.eye] || EYE_COLORS[0]);
        var lips = [Math.min(1, skin[0] * 0.92), skin[1] * 0.72, skin[2] * 0.83];
        for (var i = 0; i < V; i++) {
            var r = regions[i], c;
            if (r === 0) c = skin; else if (r === 1) c = lips; else if (r === 6) c = iris; else c = FIXED[r] || skin;
            colors[i * 3] = c[0]; colors[i * 3 + 1] = c[1]; colors[i * 3 + 2] = c[2];
        }
        if (geo && geo.attributes.color) geo.attributes.color.needsUpdate = true;
    }

    /* ───────── asset load (multi-base fallback + streamed download progress) ───────── */
    var progTotal = 0, progLoaded = 0;
    function paintProgress() {
        if (!progTotal) return;
        var pct = Math.min(99, Math.round(progLoaded / progTotal * 100));
        setStatus(L('loading', 'Loading 3D face model…') + ' ' + pct + '%');
    }
    function fetchBinFrom(base, name) {
        return fetch(base + name + ASSET_V, { mode: base.indexOf('//') !== -1 ? 'cors' : 'same-origin' }).then(function (r) {
            if (!r.ok) throw new Error('asset ' + name + ' ' + r.status);
            if (!r.body || !r.body.getReader) return r.arrayBuffer().then(function (b) { progLoaded += b.byteLength; paintProgress(); return b; });
            var reader = r.body.getReader(), chunks = [], size = 0;
            function pump() {
                return reader.read().then(function (res) {
                    if (res.done) {
                        var out = new Uint8Array(size), off = 0;
                        for (var i = 0; i < chunks.length; i++) { out.set(chunks[i], off); off += chunks[i].length; }
                        return out.buffer;
                    }
                    chunks.push(res.value); size += res.value.length;
                    progLoaded += res.value.length; paintProgress();
                    return pump();
                });
            }
            return pump();
        });
    }
    function loadFromBase(base) {
        var man;
        progLoaded = 0; progTotal = 0;
        return fetch(base + 'gnm-manifest.json' + ASSET_V, { mode: base.indexOf('//') !== -1 ? 'cors' : 'same-origin' })
            .then(function (r) { if (!r.ok) throw new Error('manifest ' + r.status); return r.json(); })
            .then(function (m) {
                man = m; V = m.V; T = m.T; K_ID = m.K_id; K_EX = m.K_ex; exGroups = m.ex_groups || [];
                progTotal = (m.K_id + m.K_ex) * m.V * 3 * 2 + m.V * 3 * 4 + m.T * 3 * 4 + m.V * 4;
                var files = [fetchBinFrom(base, m.files.template), fetchBinFrom(base, m.files.id), fetchBinFrom(base, m.files.ex), fetchBinFrom(base, m.files.tris), fetchBinFrom(base, m.files.colors)];
                files.push(m.files.regions ? fetchBinFrom(base, m.files.regions) : Promise.resolve(null));
                return Promise.all(files);
            })
            .then(function (bufs) { return { man: man, bufs: bufs }; });
    }
    function loadAssets() {
        setStatus(L('loading', 'Loading 3D face model…'));
        var t0 = performance.now();
        var attempt = Promise.reject(new Error('no bases'));
        BASES.forEach(function (base, i) {
            attempt = attempt.catch(function (prevErr) {
                if (i > 0) ae('fm_base_fallback', { from: BASES[i - 1], code: (prevErr && prevErr.message || '').slice(0, 60) });
                return loadFromBase(base).then(function (res) { activeBase = base; return res; });
            });
        });
        return attempt
            .then(function (res) {
                var man = res.man, bufs = res.bufs;
                template = new Float32Array(bufs[0]);
                var idI16 = new Int16Array(bufs[1]), exI16 = new Int16Array(bufs[2]);
                tris = new Uint32Array(bufs[3]);
                var col8 = new Uint8Array(bufs[4]);
                regions = bufs[5] ? new Uint8Array(bufs[5]) : null;
                idF = new Float32Array(idI16.length); for (var i = 0; i < idI16.length; i++) idF[i] = idI16[i] * man.id_scale;
                exF = new Float32Array(exI16.length); for (var j = 0; j < exI16.length; j++) exF[j] = exI16[j] * man.ex_scale;
                colors = new Float32Array(col8.length); for (var k = 0; k < col8.length; k++) colors[k] = col8[k] / 255;
                idC = new Float32Array(K_ID); exC = new Float32Array(K_EX); pos = new Float32Array(V * 3);
                var off = 0;
                exGroups.forEach(function (g) { if (g.region === 'pupils') pupilIdx = off; off += g.count; });
                ae('fm_assets_ok', { ms: Math.round(performance.now() - t0), V: V, K_id: K_ID, K_ex: K_EX, bytes: progLoaded, base: (activeBase && activeBase.indexOf('//') !== -1) ? 'ru-cross' : 'same-origin' });
            });
    }

    /* ───────── forward pass ───────── */
    function recompute() {
        pos.set(template);
        var i, k, o, c, n = V * 3;
        for (k = 0; k < K_ID; k++) { c = idC[k]; if (!c) continue; o = k * n; for (i = 0; i < n; i++) pos[i] += c * idF[o + i]; }
        for (k = 0; k < K_EX; k++) { c = exC[k]; if (!c) continue; o = k * n; for (i = 0; i < n; i++) pos[i] += c * exF[o + i]; }
        if (geo && geo.attributes.position) { geo.attributes.position.needsUpdate = true; geo.computeVertexNormals(); }
        for (k = 0; k < recomputeHooks.length; k++) { try { recomputeHooks[k](); } catch (_) {} }
    }

    /* ───────── scene ───────── */
    function initScene() {
        var wrap = els.canvasWrap;
        try {
            var probe = document.createElement('canvas');
            if (!(probe.getContext('webgl2') || probe.getContext('webgl'))) throw new Error('no_webgl');
            // alpha:true so the PNG export can render a transparent background.
            renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: true });
        } catch (e) {
            setStatus(L('err_webgl', 'Your browser does not support 3D (WebGL).'));
            ae('fm_webgl_unavailable', {}); return false;
        }
        renderer.outputEncoding = THREE.sRGBEncoding;
        if ('physicallyCorrectLights' in renderer) renderer.physicallyCorrectLights = true;
        renderer.domElement.id = 'fm-gl';
        renderer.domElement.setAttribute('aria-label', L('aria_preview', '3D face preview'));
        wrap.appendChild(renderer.domElement);

        scene = new THREE.Scene(); scene.background = new THREE.Color(bgColor());
        camera = new THREE.PerspectiveCamera(26, 1, 0.01, 20);
        scene.add(new THREE.HemisphereLight(0xffffff, 0x3a4250, 1.0));
        var key = new THREE.DirectionalLight(0xffffff, 1.35); key.position.set(0.5, 0.9, 1.3); scene.add(key);
        var rim = new THREE.DirectionalLight(0xbfd4ff, 0.4); rim.position.set(-1.2, 0.6, -1.2); scene.add(rim);

        if (THREE.OrbitControls) {
            controls = new THREE.OrbitControls(camera, renderer.domElement);
            controls.enablePan = false; controls.enableDamping = true; controls.dampingFactor = 0.08;
            controls.rotateSpeed = 0.7; controls.minDistance = 0.15; controls.maxDistance = 3;
        }
        window.addEventListener('resize', resize);
        return true;
    }

    function buildMesh() {
        geo = new THREE.BufferGeometry();
        recompute();
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geo.setIndex(new THREE.BufferAttribute(tris, 1));
        geo.computeVertexNormals();
        var mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.0 });
        mesh = new THREE.Mesh(geo, mat);
        scene.add(mesh);
        frameCamera(); resize();
    }

    function frameCamera() {
        if (!geo || !camera) return;
        geo.computeBoundingBox();
        var bb = geo.boundingBox.clone(), ctr = new THREE.Vector3(), sz = new THREE.Vector3();
        if (window.__fmHair && window.__fmHair.expandBox) { try { window.__fmHair.expandBox(bb); } catch (_) {} }
        bb.getCenter(ctr); bb.getSize(sz);
        var rad = Math.max(sz.x, sz.y) * 0.60 / camZoom;
        var dist = rad / Math.tan(camera.fov * Math.PI / 360);
        camera.position.set(ctr.x, ctr.y, ctr.z + dist);
        camera.lookAt(ctr);
        if (controls) { controls.target.copy(ctr); controls.update(); }
    }

    function resize() {
        if (!renderer || exporting) return;
        var wrap = els.canvasWrap, w = Math.max(160, wrap.clientWidth - 2), h = Math.min(520, Math.round(w));
        renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
        renderer.setSize(w, h, true);
        camera.aspect = w / h; camera.updateProjectionMatrix();
    }

    function bgColor() { return (els.bg && els.bg.value) || '#0f1830'; }
    function applyPose() { if (mesh) mesh.rotation.set(pose.rx, pose.ry, pose.rz); }

    function tick() {
        if (disposed) return;
        tickId = requestAnimationFrame(tick);
        if (exporting) return;
        if (controls) controls.update();
        if (renderer && scene && camera) renderer.render(scene, camera);
    }

    function setStatus(msg) { if (els.status) { els.status.textContent = msg || ''; els.status.style.display = msg ? '' : 'none'; } }

    /* ───────── share-code: face state ⇄ compact base64url in the URL hash ─────────
       Layout: [ver][K_ID int8][K_EX int8][pose rx,ry,rz int8][skin][eye] and, for
       ver=3 only, [hairStyle+1][hairColor] appended. A bald face still encodes as
       ver=2 so links stay readable by pre-hair clients. Int8 quantization of the
       −3.5..3.5 coeff range keeps a full face at ~110 chars. */
    var POSE_MAX = { rx: 0.6, ry: 0.9, rz: 0.6 };
    function hairState() {
        if (window.__fmHair && window.__fmHair.getState) return window.__fmHair.getState();
        return pendingHair;   // module absent/not booted — carry what we know
    }
    function encodeFace() {
        var hs = hairState();
        var withHair = !!(hs && hs.s >= 0);
        var bytes = [withHair ? 3 : 2], i;
        for (i = 0; i < K_ID; i++) bytes.push(Math.max(-127, Math.min(127, Math.round(idC[i] / 3.5 * 127))) & 255);
        for (i = 0; i < K_EX; i++) bytes.push(Math.max(-127, Math.min(127, Math.round(exC[i] / 3.5 * 127))) & 255);
        [pose.rx / POSE_MAX.rx, pose.ry / POSE_MAX.ry, pose.rz / POSE_MAX.rz].forEach(function (v) {
            bytes.push(Math.max(-127, Math.min(127, Math.round(v * 127))) & 255);
        });
        bytes.push(palette.skin & 255, palette.eye & 255);
        if (withHair) bytes.push((hs.s + 1) & 255, (hs.c | 0) & 255);
        var s = ''; for (i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
        return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    function decodeFace(code) {
        try {
            var s = atob(code.replace(/-/g, '+').replace(/_/g, '/'));
            var b = []; for (var i = 0; i < s.length; i++) b.push(s.charCodeAt(i));
            if ((b[0] !== 2 && b[0] !== 3) || b.length < 1 + K_ID + K_EX + 5) return false;
            var o = 1, k;
            function i8(x) { return x > 127 ? x - 256 : x; }
            for (k = 0; k < K_ID; k++) idC[k] = i8(b[o++]) / 127 * 3.5;
            for (k = 0; k < K_EX; k++) exC[k] = i8(b[o++]) / 127 * 3.5;
            pose.rx = i8(b[o++]) / 127 * POSE_MAX.rx;
            pose.ry = i8(b[o++]) / 127 * POSE_MAX.ry;
            pose.rz = i8(b[o++]) / 127 * POSE_MAX.rz;
            palette.skin = Math.min(SKIN_TONES.length - 1, b[o++] || 0);
            palette.eye = Math.min(EYE_COLORS.length - 1, b[o++] || 0);
            if (b[0] === 3 && b.length >= o + 2) pendingHair = { s: (b[o] | 0) - 1, c: b[o + 1] | 0 };
            else pendingHair = { s: -1, c: 1 };
            if (window.__fmHair && window.__fmHair.applyState) window.__fmHair.applyState(pendingHair);
            return true;
        } catch (_) { return false; }
    }
    var hashTimer = 0;
    function updateHashSoon() {
        if (hashTimer) clearTimeout(hashTimer);
        hashTimer = setTimeout(function () {
            try { history.replaceState(null, '', '#f=' + encodeFace()); } catch (_) {}
        }, 500);
    }
    function readHash() {
        var m = /[#&]f=([A-Za-z0-9_-]+)/.exec(location.hash || '');
        return m ? m[1] : null;
    }
    function shareFace() {
        try { history.replaceState(null, '', '#f=' + encodeFace()); } catch (_) {}
        var url = location.href;
        var done = function () { toast(L('share_copied', 'Link copied — send it to anyone.'), 'success'); ae('fm_share', {}); };
        if (navigator.share) { navigator.share({ url: url }).then(done).catch(function () {}); return; }
        if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(url).then(done).catch(function () { window.prompt('URL', url); }); return; }
        window.prompt('URL', url);
    }

    /* ───────── UI build ───────── */
    var firstAdjust = false;
    function el(tag, cls, txt) { var e = document.createElement(tag); if (cls) e.className = cls; if (txt != null) e.textContent = txt; return e; }
    function sliderRow(label, min, max, val, oninput) {
        var row = el('div', 'fm-row');
        row.appendChild(el('span', 'fm-rl', label));
        var inp = el('input', 'fm-range'); inp.type = 'range'; inp.min = min; inp.max = max; inp.step = 0.05; inp.value = val;
        var out = el('span', 'fm-val', '');
        function paint() {
            var v = +inp.value, p = (v - min) / (max - min) * 100;
            inp.style.setProperty('--p', p + '%');
            // the global stylesheet paints input[type=range] via --range-fill — feed it
            // the same % so the two layers can never disagree (Farid's double-fill bug)
            inp.style.setProperty('--range-fill', p + '%');
            out.textContent = v.toFixed(1);
        }
        inp._paint = paint;
        inp.addEventListener('input', function () {
            paint();
            if (!firstAdjust) { firstAdjust = true; ae('fm_first_adjust', {}); }
            oninput(+inp.value, inp);
            saveFaceSoon(); updateHashSoon();
        });
        inp.addEventListener('dblclick', function () { inp.value = val; paint(); oninput(+inp.value, inp); saveFaceSoon(); updateHashSoon(); });
        row.appendChild(inp); row.appendChild(out);
        paint();
        return { row: row, input: inp };
    }
    function groupTitle(t) { return el('div', 'fm-gt', t); }

    /* ───────── face persistence (survives reload) ───────── */
    var saveTimer = 0;
    function saveFaceSoon() {
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(function () {
            try {
                if (!window.__toolSettings || !idC) return;
                var hs = hairState();
                window.__toolSettings.save(SLUG, 'face', JSON.stringify({
                    id: Array.prototype.slice.call(idC), ex: Array.prototype.slice.call(exC),
                    pose: { rx: pose.rx, ry: pose.ry, rz: pose.rz }, pal: { s: palette.skin, e: palette.eye },
                    hair: hs && hs.s >= 0 ? { s: hs.s, c: hs.c } : undefined
                }));
            } catch (_) {}
        }, 400);
    }
    function restoreFace() {
        try {
            if (!window.__toolSettings) return false;
            var raw = window.__toolSettings.load(SLUG, 'face', null);
            if (!raw) return false;
            var s = JSON.parse(raw);
            if (!s || !s.id || !s.ex) return false;
            for (var i = 0; i < Math.min(K_ID, s.id.length); i++) idC[i] = +s.id[i] || 0;
            for (var j = 0; j < Math.min(K_EX, s.ex.length); j++) exC[j] = +s.ex[j] || 0;
            if (s.pose) pose = { rx: +s.pose.rx || 0, ry: +s.pose.ry || 0, rz: +s.pose.rz || 0 };
            if (s.pal) { palette.skin = Math.min(SKIN_TONES.length - 1, s.pal.s | 0); palette.eye = Math.min(EYE_COLORS.length - 1, s.pal.e | 0); }
            if (s.hair && typeof s.hair.s === 'number') pendingHair = { s: s.hair.s | 0, c: s.hair.c | 0 };
            return true;
        } catch (_) { return false; }
    }

    /* ───────── presets — curated faces (id coeffs [, expression tweaks]) ─────────
       Empirically curated 2026-07-16 (12 candidates screenshotted, 6 most distinct
       kept). Every preset carries ex[0]=ex[8]=1.5 — the eye-region comps that lift
       the model's relaxed ("sleepy") eyelids into an awake look. */
    var AWAKE = [1.5, 0, 0, 0, 0, 0, 0, 0, 1.5];
    var PRESETS = (window.__FM_PRESETS && window.__FM_PRESETS.length) ? window.__FM_PRESETS : [
        { ex: AWAKE },   // neutral, eyes open
        { id: [1.02, 2.04, -1.52, 0.41, 1.76, 0.72, 1.1, 0.61, 0.99, -0.55, -0.66, 0.08, 0.03, -0.17, -0.28, -0.55, 0.19, 0.41, -0.94, -0.55, -0.8, 0.99, -0.63, -0.33, 0, 0.22, -0.83, 0.11, -0.8, -0.5], ex: AWAKE },
        { id: [2.04, -2.18, -0.74, 0.72, -1.43, 1.6, -2.04, -2.18, -0.36, 0.39, 0.63, 0.41, 0.72, 0.41, -0.25, 0.39, -0.66, -0.08, 0.94, -0.03, -0.22, -0.41, 0.96, -0.61, -0.41, -0.22, -0.33, 0.99, 0.41, -0.17], ex: AWAKE },
        { id: [1.54, -1.57, 0.39, 0.96, -0.03, 0.3, -1.07, 1.49, -0.08, -0.39, 0.25, 0.39, -0.11, 0.3, -0.28, -0.36, 0.72, -0.3, -0.61, 0.69, -0.33, 0.33, -0.36, 0.17, -0.96, 0.19, -0.8, 0.39, -0.47, -0.3], ex: AWAKE },
        { id: [-2.07, -1.6, 2.01, -1.76, -1.65, 1.16, -2.18, 1.43, -0.28, -0.5, -0.22, -0.91, 0.39, 0.66, -0.91, -0.28, -0.41, -0.5, 0.47, 0.66, -0.39, -0.52, 0.22, -0.55, 0.52, -0.03, 0.36, 0.96, -0.52, -0.8], ex: AWAKE },
        { id: [-1.74, 0.5, 0.99, 1.87, -1.68, -0.88, -0.08, 1.16, 0.99, 0.19, -0.61, -0.08, -0.14, -0.8, -0.03, -0.52, 0.36, 0.74, -0.94, 0.5, 0.19, -0.94, 0.14, -0.85, -0.94, 0.14, -0.85, 0.06, -0.3, -0.8], ex: AWAKE },
        { id: [2.2, 2.18, -0.33, 1.71, 2.07, -0.22, 0.58, 1.32, -0.8, 0.83, -0.66, 0.94, 0.91, 0.06, 0.88, 0.8, -0.52, -0.63, -0.58, 0.11, 0.41, -0.3, -0.17, -0.33, 0.17, 0.36, -0.14, 0.66, -0.55, -0.55], ex: AWAKE }
    ];
    function applyPreset(p, index) {
        idC.fill(0); exC.fill(0);
        (p.id || []).forEach(function (v, k) { if (k < K_ID) idC[k] = v; });
        (p.ex || []).forEach(function (v, k) { if (k < K_EX) exC[k] = v; });
        recompute(); syncInputs(); saveFaceSoon(); updateHashSoon();
        ae('fm_preset', { i: index });
    }
    // Live 3D thumbnails: render each preset once into a small canvas at boot.
    function buildPresetThumbs() {
        if (!els.presets) return;
        var savedId = new Float32Array(idC), savedEx = new Float32Array(exC);
        var size = 72;
        PRESETS.forEach(function (p, i) {
            idC.fill(0); exC.fill(0);
            (p.id || []).forEach(function (v, k) { if (k < K_ID) idC[k] = v; });
            (p.ex || []).forEach(function (v, k) { if (k < K_EX) exC[k] = v; });
            recompute();
            renderer.setSize(size, size, false); camera.aspect = 1; camera.updateProjectionMatrix(); frameCamera();
            renderer.render(scene, camera);
            var img = document.createElement('canvas'); img.width = size; img.height = size;
            img.getContext('2d').drawImage(renderer.domElement, 0, 0, size, size);
            var btn = el('button', 'fm-preset'); btn.type = 'button';
            btn.setAttribute('aria-label', L('preset', 'Preset') + ' ' + (i + 1));
            btn.appendChild(img);
            btn.addEventListener('click', function () { applyPreset(p, i); });
            els.presets.appendChild(btn);
        });
        idC.set(savedId); exC.set(savedEx);
        recompute(); resize(); frameCamera();
    }

    function buildUI() {
        // Identity tab
        var idTab = els.tabId, nId = Math.min(12, K_ID);
        idTab.appendChild(groupTitle(L('g_shape', 'Face shape')));
        var idInputs = [];
        for (var i = 0; i < nId; i++) (function (k) {
            var r = sliderRow(L('shape', 'Shape') + ' ' + (k + 1), -3.5, 3.5, 0, function (v) { idC[k] = v; recompute(); });
            idInputs.push(r.input); idTab.appendChild(r.row);
        })(i);

        // Color tab: skin tones, eye colors, pupil size
        var cTab = els.tabColor;
        cTab.appendChild(groupTitle(L('skin_tone', 'Skin tone')));
        var skinRow = el('div', 'fm-swatches');
        SKIN_TONES.forEach(function (hex, i) {
            var b = el('button', 'fm-swatch'); b.type = 'button'; b.style.background = hex;
            b.setAttribute('aria-label', L('skin_tone', 'Skin tone') + ' ' + (i + 1));
            b.addEventListener('click', function () { palette.skin = i; computeColors(); markSwatches(); saveFaceSoon(); updateHashSoon(); ae('fm_palette', { skin: i }); });
            skinRow.appendChild(b);
        });
        cTab.appendChild(skinRow);
        cTab.appendChild(groupTitle(L('eye_color', 'Eye color')));
        var eyeRow = el('div', 'fm-swatches');
        EYE_COLORS.forEach(function (hex, i) {
            var b = el('button', 'fm-swatch'); b.type = 'button'; b.style.background = hex;
            b.setAttribute('aria-label', L('eye_color', 'Eye color') + ' ' + (i + 1));
            b.addEventListener('click', function () { palette.eye = i; computeColors(); markSwatches(); saveFaceSoon(); updateHashSoon(); ae('fm_palette', { eye: i }); });
            eyeRow.appendChild(b);
        });
        cTab.appendChild(eyeRow);
        els._skinRow = skinRow; els._eyeRow = eyeRow;
        if (pupilIdx >= 0) {
            cTab.appendChild(groupTitle(L('g_pupils', 'Pupils')));
            var pr = sliderRow(L('pupil_size', 'Pupil size'), -3.5, 3.5, 0, function (v) { exC[pupilIdx] = v; recompute(); });
            els._pupil = pr.input; cTab.appendChild(pr.row);
        }

        // Expression tab
        var exTab = els.tabExpr, exInputs = [], base = 0;
        var GLAB = { left_eye_region: L('g_lefteye', 'Left eye'), right_eye_region: L('g_righteye', 'Right eye'), lower_face_region: L('g_mouth', 'Mouth & face'), tongue: L('g_tongue', 'Tongue') };
        exGroups.forEach(function (g) {
            if (g.region === 'pupils') { base += g.count; return; }   // lives in the Color tab
            exTab.appendChild(groupTitle(GLAB[g.region] || g.region));
            for (var j = 0; j < g.count; j++) (function (k, lbl) {
                var r = sliderRow(lbl + ' ' + (j + 1), -3.5, 3.5, 0, function (v) { exC[k] = v; recompute(); });
                exInputs.push({ input: r.input, k: k }); exTab.appendChild(r.row);
            })(base + j, GLAB[g.region] || g.region);
            base += g.count;
        });

        // Pose tab
        var poseTab = els.tabPose;
        poseTab.appendChild(groupTitle(L('g_head', 'Head pose')));
        var poseInputs = {};
        [['ry', L('yaw', 'Turn')], ['rx', L('pitch', 'Nod')], ['rz', L('roll', 'Tilt')]].forEach(function (a) {
            var r = sliderRow(a[1], -100, 100, 0, function (v) { pose[a[0]] = (v / 100) * POSE_MAX[a[0]]; applyPose(); });
            poseInputs[a[0]] = r.input; poseTab.appendChild(r.row);
        });
        var rz = sliderRow(L('zoom', 'Zoom'), 70, 180, 100, function (v) { camZoom = v / 100; frameCamera(); });
        poseTab.appendChild(rz.row);

        els._idInputs = idInputs; els._exInputs = exInputs; els._poseInputs = poseInputs; els._zoom = rz.input;
        markSwatches();
    }

    function markSwatches() {
        if (els._skinRow) Array.prototype.forEach.call(els._skinRow.children, function (b, i) { b.classList.toggle('active', i === palette.skin); });
        if (els._eyeRow) Array.prototype.forEach.call(els._eyeRow.children, function (b, i) { b.classList.toggle('active', i === palette.eye); });
    }

    function syncInputs() {
        var i;
        for (i = 0; i < els._idInputs.length; i++) els._idInputs[i].value = idC[i] || 0;
        for (i = 0; i < els._exInputs.length; i++) els._exInputs[i].input.value = exC[els._exInputs[i].k] || 0;
        els._poseInputs.rx.value = Math.round(pose.rx / POSE_MAX.rx * 100);
        els._poseInputs.ry.value = Math.round(pose.ry / POSE_MAX.ry * 100);
        els._poseInputs.rz.value = Math.round(pose.rz / POSE_MAX.rz * 100);
        if (els._pupil && pupilIdx >= 0) els._pupil.value = exC[pupilIdx] || 0;
        var all = els._idInputs.concat(els._exInputs.map(function (x) { return x.input; }), [els._poseInputs.rx, els._poseInputs.ry, els._poseInputs.rz, els._zoom, els._pupil]);
        for (i = 0; i < all.length; i++) if (all[i] && all[i]._paint) all[i]._paint();
        applyPose(); computeColors(); markSwatches();
    }

    var _seed = 1;
    function rnd() { _seed = (_seed * 1103515245 + 12345) & 0x7fffffff; return _seed / 0x7fffffff; }
    function randomFace() {
        _seed = (Date.now ? (performance.now() * 1000) : 1) & 0x7fffffff | 1;
        for (var k = 0; k < K_ID; k++) idC[k] = (rnd() * 2 - 1) * (k < 8 ? 2.2 : 1.0);
        recompute(); syncInputs(); saveFaceSoon(); updateHashSoon();
        ae('fm_random', {});
    }
    function resetAll() {
        idC.fill(0); exC.fill(0); pose = { rx: 0, ry: 0, rz: 0 }; camZoom = 1;
        palette = { skin: 2, eye: 0 };
        pendingHair = null;
        if (window.__fmHair && window.__fmHair.reset) { try { window.__fmHair.reset(); } catch (_) {} }
        recompute(); applyPose(); frameCamera(); syncInputs();
        if (els._zoom) { els._zoom.value = 100; if (els._zoom._paint) els._zoom._paint(); }
        try { history.replaceState(null, '', location.pathname + location.search); } catch (_) {}
        saveFaceSoon();
        ae('fm_reset', {});
    }

    function tab(name) {
        ['id', 'color', 'expr', 'pose'].forEach(function (t) {
            var on = t === name;
            var pane = { id: 'tabId', color: 'tabColor', expr: 'tabExpr', pose: 'tabPose' }[t];
            els[pane].style.display = on ? '' : 'none';
            els['btn_' + t].classList.toggle('active', on);
            els['btn_' + t].setAttribute('aria-selected', on ? 'true' : 'false');
        });
        extraTabs.forEach(function (t) {
            var on = t.name === name;
            t.pane.style.display = on ? '' : 'none';
            t.btn.classList.toggle('active', on);
            t.btn.setAttribute('aria-selected', on ? 'true' : 'false');
        });
    }

    // Optional modules (hair.js) plug their own tab in; the blade ships only the
    // four core tabs, so a missing/failed module simply never shows its tab.
    function registerTab(name, label) {
        var btn = el('button', 'fm-tab'); btn.type = 'button'; btn.id = 'fm-btn-' + name;
        btn.setAttribute('role', 'tab'); btn.setAttribute('aria-selected', 'false');
        btn.textContent = label;
        var pane = el('div', 'fm-panel'); pane.id = 'fm-tab-' + name; pane.style.display = 'none';
        els.btn_id.parentNode.appendChild(btn);
        els.btn_id.parentNode.classList.add('fm-tabs-x5');   // 5-column tab grid
        els.tabId.parentNode.appendChild(pane);
        btn.addEventListener('click', function () { tab(name); ae('fm_tab', { tab: name }); });
        extraTabs.push({ name: name, btn: btn, pane: pane });
        return { btn: btn, pane: pane };
    }

    /* ───────── export: PNG ───────── */
    function dl(blob, name) {
        (window.__downloadFile || function (b, n) { var u = URL.createObjectURL(b); var a = document.createElement('a'); a.href = u; a.download = n; a.click(); URL.revokeObjectURL(u); })(blob, name);
    }
    function exportPng() {
        if (!renderer || exporting) return;
        var t0 = performance.now();
        var transparent = !!(els.transparent && els.transparent.checked);
        var prevPR = renderer.getPixelRatio(), prevSz = new THREE.Vector2(); renderer.getSize(prevSz);
        var prevBg = scene.background;
        var S = 1080; renderer.setPixelRatio(1); renderer.setSize(S, S, false);
        camera.aspect = 1; camera.updateProjectionMatrix(); frameCamera();
        if (transparent) { scene.background = null; renderer.setClearColor(0x000000, 0); }
        renderer.render(scene, camera);
        renderer.domElement.toBlob(function (blob) {
            scene.background = prevBg;
            renderer.setClearColor(new THREE.Color(bgColor()), 1);
            renderer.setPixelRatio(prevPR); renderer.setSize(prevSz.x, prevSz.y, false);
            camera.aspect = prevSz.x / prevSz.y; camera.updateProjectionMatrix(); frameCamera();
            if (!blob) { toast(L('err_export', 'Could not export the image.'), 'error'); ae('fm_export_err', { code: 'toblob_null' }); return; }
            // AI Act art. 50(2): PNG carries machine-readable AI-note tEXt
            // chunks (synthetic face); marking failure falls back to the raw blob.
            var finish = function (b) {
                dl(b || blob, 'face-' + Date.now() + '.png');
                toast(L('export_ok', 'Saved.'), 'success');
                ae('fm_export_png', { ms: Math.round(performance.now() - t0), transparent: transparent, bytes: (b || blob).size });
            };
            if (window.__aiMark) { window.__aiMark.png(blob, 'face-maker').then(finish, function () { finish(blob); }); }
            else { finish(blob); }
        }, 'image/png');
    }

    /* ───────── export: OBJ / GLB (mesh with vertex colors) ───────── */
    function exportObj() {
        if (!pos || exporting) return;
        var t0 = performance.now();
        var out = ['# Timbrica face-maker mesh', 'o face'];
        for (var i = 0; i < V; i++) {
            // vertex-color OBJ extension (v x y z r g b) — Blender/MeshLab import it
            out.push('v ' + pos[i * 3].toFixed(5) + ' ' + pos[i * 3 + 1].toFixed(5) + ' ' + pos[i * 3 + 2].toFixed(5) + ' ' +
                colors[i * 3].toFixed(3) + ' ' + colors[i * 3 + 1].toFixed(3) + ' ' + colors[i * 3 + 2].toFixed(3));
        }
        for (var t = 0; t < T; t++) out.push('f ' + (tris[t * 3] + 1) + ' ' + (tris[t * 3 + 1] + 1) + ' ' + (tris[t * 3 + 2] + 1));
        var hg = (window.__fmHair && window.__fmHair.exportGeometry) ? window.__fmHair.exportGeometry() : null;
        if (hg) {
            out.push('o hair');
            var hc = hg.color[0].toFixed(3) + ' ' + hg.color[1].toFixed(3) + ' ' + hg.color[2].toFixed(3);
            for (var hv = 0; hv < hg.nv; hv++)
                out.push('v ' + hg.pos[hv * 3].toFixed(5) + ' ' + hg.pos[hv * 3 + 1].toFixed(5) + ' ' + hg.pos[hv * 3 + 2].toFixed(5) + ' ' + hc);
            for (var ht = 0; ht < hg.nt; ht++)
                out.push('f ' + (hg.tris[ht * 3] + 1 + V) + ' ' + (hg.tris[ht * 3 + 1] + 1 + V) + ' ' + (hg.tris[ht * 3 + 2] + 1 + V));
        }
        var blob = new Blob([out.join('\n')], { type: 'text/plain' });
        dl(blob, 'face-' + Date.now() + '.obj');
        toast(L('export_ok', 'Saved.'), 'success');
        ae('fm_export_obj', { ms: Math.round(performance.now() - t0), bytes: blob.size });
    }
    function exportGlb() {
        if (!pos || !geo || exporting) return;
        var t0 = performance.now();
        geo.computeVertexNormals();
        var normals = geo.attributes.normal.array;
        var hg = (window.__fmHair && window.__fmHair.exportGeometry) ? window.__fmHair.exportGeometry() : null;
        var idxBytes = T * 3 * 4, vBytes = V * 3 * 4;
        var hIdxBytes = hg ? hg.nt * 3 * 4 : 0, hVBytes = hg ? hg.nv * 3 * 4 : 0;
        var pad = function (n) { return (4 - (n % 4)) % 4; };
        var binLen = idxBytes + vBytes * 3 + hIdxBytes + hVBytes * 2;
        var bin = new ArrayBuffer(binLen);
        new Uint32Array(bin, 0, T * 3).set(tris);
        new Float32Array(bin, idxBytes, V * 3).set(pos);
        new Float32Array(bin, idxBytes + vBytes, V * 3).set(normals);
        new Float32Array(bin, idxBytes + vBytes * 2, V * 3).set(colors);
        var mn = [1e9, 1e9, 1e9], mx = [-1e9, -1e9, -1e9];
        for (var i = 0; i < V; i++) for (var a = 0; a < 3; a++) { var v = pos[i * 3 + a]; if (v < mn[a]) mn[a] = v; if (v > mx[a]) mx[a] = v; }
        var gltf = {
            asset: { version: '2.0', generator: 'Timbrica face-maker' },
            scene: 0, scenes: [{ nodes: [0] }], nodes: [{ mesh: 0, name: 'face' }],
            meshes: [{ primitives: [{ attributes: { POSITION: 1, NORMAL: 2, COLOR_0: 3 }, indices: 0, material: 0 }] }],
            materials: [{ pbrMetallicRoughness: { baseColorFactor: [1, 1, 1, 1], metallicFactor: 0, roughnessFactor: 0.72 } }],
            buffers: [{ byteLength: binLen }],
            bufferViews: [
                { buffer: 0, byteOffset: 0, byteLength: idxBytes, target: 34963 },
                { buffer: 0, byteOffset: idxBytes, byteLength: vBytes, target: 34962 },
                { buffer: 0, byteOffset: idxBytes + vBytes, byteLength: vBytes, target: 34962 },
                { buffer: 0, byteOffset: idxBytes + vBytes * 2, byteLength: vBytes, target: 34962 }
            ],
            accessors: [
                { bufferView: 0, componentType: 5125, count: T * 3, type: 'SCALAR' },
                { bufferView: 1, componentType: 5126, count: V, type: 'VEC3', min: mn, max: mx },
                { bufferView: 2, componentType: 5126, count: V, type: 'VEC3' },
                { bufferView: 3, componentType: 5126, count: V, type: 'VEC3' }
            ]
        };
        if (hg) {
            // hair as a second primitive: u32 indices + positions + normals, solid
            // hair color via the material factor (no per-vertex colors needed)
            var hOff = idxBytes + vBytes * 3;
            var hIdx = new Uint32Array(bin, hOff, hg.nt * 3);
            for (var q = 0; q < hg.nt * 3; q++) hIdx[q] = hg.tris[q];
            new Float32Array(bin, hOff + hIdxBytes, hg.nv * 3).set(hg.pos);
            new Float32Array(bin, hOff + hIdxBytes + hVBytes, hg.nv * 3).set(hg.normal);
            var hmn = [1e9, 1e9, 1e9], hmx = [-1e9, -1e9, -1e9];
            for (var hi = 0; hi < hg.nv; hi++) for (var ha = 0; ha < 3; ha++) { var hv = hg.pos[hi * 3 + ha]; if (hv < hmn[ha]) hmn[ha] = hv; if (hv > hmx[ha]) hmx[ha] = hv; }
            gltf.meshes[0].primitives.push({ attributes: { POSITION: 5, NORMAL: 6 }, indices: 4, material: 1 });
            gltf.materials.push({ pbrMetallicRoughness: { baseColorFactor: [hg.color[0], hg.color[1], hg.color[2], 1], metallicFactor: 0.05, roughnessFactor: 0.62 }, doubleSided: true });
            gltf.bufferViews.push(
                { buffer: 0, byteOffset: hOff, byteLength: hIdxBytes, target: 34963 },
                { buffer: 0, byteOffset: hOff + hIdxBytes, byteLength: hVBytes, target: 34962 },
                { buffer: 0, byteOffset: hOff + hIdxBytes + hVBytes, byteLength: hVBytes, target: 34962 }
            );
            gltf.accessors.push(
                { bufferView: 4, componentType: 5125, count: hg.nt * 3, type: 'SCALAR' },
                { bufferView: 5, componentType: 5126, count: hg.nv, type: 'VEC3', min: hmn, max: hmx },
                { bufferView: 6, componentType: 5126, count: hg.nv, type: 'VEC3' }
            );
        }
        var jsonU8 = new TextEncoder().encode(JSON.stringify(gltf));
        var jsonPad = pad(jsonU8.length), binPad = pad(binLen);
        var total = 12 + 8 + jsonU8.length + jsonPad + 8 + binLen + binPad;
        var out = new ArrayBuffer(total);
        var dv = new DataView(out); var u8 = new Uint8Array(out);
        dv.setUint32(0, 0x46546C67, true); dv.setUint32(4, 2, true); dv.setUint32(8, total, true);
        dv.setUint32(12, jsonU8.length + jsonPad, true); dv.setUint32(16, 0x4E4F534A, true);
        u8.set(jsonU8, 20);
        for (var p = 0; p < jsonPad; p++) u8[20 + jsonU8.length + p] = 0x20;
        var binOff = 20 + jsonU8.length + jsonPad;
        dv.setUint32(binOff, binLen + binPad, true); dv.setUint32(binOff + 4, 0x004E4942, true);
        u8.set(new Uint8Array(bin), binOff + 8);
        var blob = new Blob([out], { type: 'model/gltf-binary' });
        dl(blob, 'face-' + Date.now() + '.glb');
        toast(L('export_ok', 'Saved.'), 'success');
        ae('fm_export_glb', { ms: Math.round(performance.now() - t0), bytes: blob.size });
    }

    /* ───────── export: turntable video (WebCodecs → MediaRecorder fallback) ───────── */
    var DUR = 4, FPS = 30, VS = 1080;
    function drawTurntableFrame(t) {
        if (mesh) mesh.rotation.set(pose.rx, pose.ry + t / DUR * Math.PI * 2, pose.rz);
        renderer.render(scene, camera);
    }
    function pickRecorderMime() {
        if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return null;
        var cands = ['video/mp4;codecs=avc1.640028', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
        for (var i = 0; i < cands.length; i++) if (MediaRecorder.isTypeSupported(cands[i])) return cands[i];
        return null;
    }
    function exportVideo() {
        if (!renderer || exporting) return;
        var t0 = performance.now();
        exporting = true;
        if (els.video) els.video.disabled = true;
        setStatus(L('video_making', 'Rendering video…'));
        var prevPR = renderer.getPixelRatio(), prevSz = new THREE.Vector2(); renderer.getSize(prevSz);
        renderer.setPixelRatio(1); renderer.setSize(VS, VS, false);
        camera.aspect = 1; camera.updateProjectionMatrix(); frameCamera();

        function restore() {
            exporting = false;
            if (els.video) els.video.disabled = false;
            setStatus('');
            if (mesh) applyPose();
            renderer.setPixelRatio(prevPR); renderer.setSize(prevSz.x, prevSz.y, false);
            camera.aspect = prevSz.x / prevSz.y; camera.updateProjectionMatrix(); frameCamera();
        }
        function ok(blob, fmt, path) {
            restore();
            var name = 'face-turntable-' + Date.now() + '.' + fmt;
            // AI Act art. 50(2): MP4 turntable gets the moov udta AI-note;
            // WebM passes through; marking failure falls back to the raw blob.
            var send = function (b) {
                b = b || blob;
                dl(b, name);
                toast(L('export_ok', 'Saved.'), 'success');
                ae('fm_export_video', { ms: Math.round(performance.now() - t0), fmt: fmt, path: path, bytes: b.size });
            };
            if (window.__aiMark) { window.__aiMark.auto(blob, name, 'face-maker').then(send, function () { send(blob); }); }
            else { send(blob); }
        }
        function fail(err, code) {
            restore();
            console.error('[face-maker] video export failed', err);
            toast(L('err_video', 'Video export failed in this browser.'), 'error');
            ae('fm_export_err', { code: code || 'video', msg: (err && err.message || '').slice(0, 60) });
        }

        var CVE = window.CanvasVideoExport;
        var probe = CVE ? CVE.isSupported({ width: VS, height: VS, fps: FPS, format: 'mp4', audioSampleRate: 44100, audioChannels: 2 }).catch(function () { return { ok: false }; }) : Promise.resolve({ ok: false });
        probe.then(function (support) {
            var fmt = 'mp4';
            var next = (support && support.ok) ? Promise.resolve(true) : (CVE ? CVE.isSupported({ width: VS, height: VS, fps: FPS, format: 'webm', audioSampleRate: 44100, audioChannels: 2 }).then(function (s) { fmt = 'webm'; return s.ok; }).catch(function () { return false; }) : Promise.resolve(false));
            next.then(function (useCve) {
                if (useCve) {
                    CVE.encode({
                        width: VS, height: VS, fps: FPS, durationSec: DUR, format: fmt, qualityCrf: 21, audioBuffer: null,
                        drawFrame: function (ctx, fi, t) { drawTurntableFrame(Math.min(t, DUR)); ctx.drawImage(renderer.domElement, 0, 0, VS, VS); },
                        onProgress: function (f) { setStatus(L('video_making', 'Rendering video…') + ' ' + Math.round(f * 100) + '%'); }
                    }).then(function (blob) { ok(blob, fmt, 'webcodecs'); }).catch(function (e) { fail(e, 'webcodecs'); });
                    return;
                }
                var mime = pickRecorderMime();
                if (!mime) { fail(new Error('unsupported'), 'unsupported'); return; }
                // The first video recorder of a Chromium tab drops ~2 s of
                // frames — most of the turntable. Warm the encoder first.
                (window.__mediaRecorderWarm ? window.__mediaRecorderWarm({ width: VS, height: VS, mimeType: mime, videoBitsPerSecond: VS * VS * 4 }) : Promise.resolve()).then(function () {
                if (disposed) return;
                try {
                    var comp = document.createElement('canvas'); comp.width = VS; comp.height = VS;
                    var cctx = comp.getContext('2d');
                    var stream = comp.captureStream(FPS);
                    var rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: VS * VS * 4 });
                    var chunks = [];
                    rec.ondataavailable = function (e) { if (e.data && e.data.size) chunks.push(e.data); };
                    rec.onstop = function () {
                        var type = mime.split(';')[0];
                        ok(new Blob(chunks, { type: type }), type.indexOf('mp4') !== -1 ? 'mp4' : 'webm', 'mediarecorder');
                    };
                    var start = performance.now();
                    rec.start();
                    (function step() {
                        var t = (performance.now() - start) / 1000;
                        if (t >= DUR || disposed) { try { rec.stop(); } catch (_) {} return; }
                        drawTurntableFrame(t);
                        cctx.drawImage(renderer.domElement, 0, 0, VS, VS);
                        setStatus(L('video_making', 'Rendering video…') + ' ' + Math.round(t / DUR * 100) + '%');
                        requestAnimationFrame(step);
                    })();
                } catch (e) { fail(e, 'mediarecorder'); }
                });
            });
        });
    }

    /* ───────── boot ───────── */
    function boot() {
        els = {
            canvasWrap: $('fm-canvas'), status: $('fm-status'), bg: $('fm-bg'),
            stage: $('fm-stage'), spec: $('fm-spec'), transparent: $('fm-transparent'),
            presets: $('fm-presets'),
            tabId: $('fm-tab-identity'), tabColor: $('fm-tab-color'), tabExpr: $('fm-tab-expression'), tabPose: $('fm-tab-pose'),
            btn_id: $('fm-btn-identity'), btn_color: $('fm-btn-color'), btn_expr: $('fm-btn-expression'), btn_pose: $('fm-btn-pose'),
            random: $('fm-random'), reset: $('fm-reset'), download: $('fm-download'),
            share: $('fm-share'), glb: $('fm-glb'), obj: $('fm-obj'), video: $('fm-video')
        };
        if (!els.canvasWrap) return;
        try { if (window.Timbrica3D && Timbrica3D.init) Timbrica3D.init(SLUG); } catch (_) {}

        try {
            if (window.__toolSettings) {
                var sb = window.__toolSettings.load(SLUG, 'bg', null);
                if (sb && els.bg) els.bg.value = sb;
                if (els.transparent) els.transparent.checked = window.__toolSettings.load(SLUG, 'transparent', '0') === '1';
            }
        } catch (_) {}

        if (!initScene()) return;
        tickId = requestAnimationFrame(tick);

        loadAssets().then(function () {
            buildMesh(); buildUI();
            // A shared link wins over the locally saved face.
            var shared = readHash();
            if (shared && decodeFace(shared)) { recompute(); ae('fm_open_shared', {}); }
            else if (restoreFace()) { recompute(); ae('fm_restore', {}); }
            syncInputs(); setStatus(''); tab('id');
            buildPresetThumbs();
            // Optional hair module — isolated: any failure here leaves the tool
            // fully functional, the Hair tab just never appears.
            if (window.__fmHairModule && window.__fmHairModule.init) {
                try {
                    window.__fmHairModule.init({
                        THREE: THREE, pos: pos, template: template, tris: tris, V: V, T: T,
                        mesh: mesh, scene: scene,
                        bases: BASES, activeBase: activeBase,
                        assetV: (window.__FM_HAIR_V ? ('?v=' + window.__FM_HAIR_V) : ASSET_V),
                        L: L, ae: ae,
                        registerTab: registerTab,
                        onRecompute: function (cb) { recomputeHooks.push(cb); },
                        requestSave: function () { saveFaceSoon(); updateHashSoon(); },
                        pendingState: pendingHair
                    });
                } catch (e) {
                    console.error('[face-maker] hair module failed', e);
                    ae('fm_hair_err', { code: 'init', msg: (e && e.message || '').slice(0, 60) });
                }
            }
            if (els.stage) { els.stage.classList.remove('loading'); els.stage.classList.add('ready'); }
            if (els.spec) { try { els.spec.textContent = V.toLocaleString() + ' · 3DMM'; } catch (_) { els.spec.textContent = V + ' · 3DMM'; } }
            els.btn_id.addEventListener('click', function () { tab('id'); ae('fm_tab', { tab: 'id' }); });
            els.btn_color.addEventListener('click', function () { tab('color'); ae('fm_tab', { tab: 'color' }); });
            els.btn_expr.addEventListener('click', function () { tab('expr'); ae('fm_tab', { tab: 'expr' }); });
            els.btn_pose.addEventListener('click', function () { tab('pose'); ae('fm_tab', { tab: 'pose' }); });
            if (els.random) els.random.addEventListener('click', randomFace);
            if (els.reset) els.reset.addEventListener('click', resetAll);
            if (els.download) els.download.addEventListener('click', exportPng);
            if (els.share) els.share.addEventListener('click', shareFace);
            if (els.glb) els.glb.addEventListener('click', exportGlb);
            if (els.obj) els.obj.addEventListener('click', exportObj);
            if (els.video) {
                // Hide the button when NEITHER export path exists (no WebCodecs and no
                // MediaRecorder/captureStream) — a dead button is worse than no button.
                var canVideo = (typeof VideoEncoder !== 'undefined') ||
                    (typeof MediaRecorder !== 'undefined' && !!(window.HTMLCanvasElement && HTMLCanvasElement.prototype.captureStream));
                if (!canVideo) els.video.style.display = 'none';
                else els.video.addEventListener('click', exportVideo);
            }
            if (els.bg) els.bg.addEventListener('input', function () {
                if (scene) scene.background = new THREE.Color(bgColor());
                try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'bg', els.bg.value); } catch (_) {}
            });
            if (els.transparent) els.transparent.addEventListener('change', function () {
                try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'transparent', els.transparent.checked ? '1' : '0'); } catch (_) {}
            });
            window.__fm = {
                ready: true, V: V, K_ID: K_ID, K_EX: K_EX, base: activeBase, pupilIdx: pupilIdx,
                setId: function (k, v) { idC[k] = v; recompute(); syncInputs(); },
                setEx: function (k, v) { exC[k] = v; recompute(); syncInputs(); },
                setPalette: function (s, e) { palette.skin = s; palette.eye = e; computeColors(); markSwatches(); },
                encode: encodeFace, decode: function (c) { var r = decodeFace(c); if (r) { recompute(); syncInputs(); } return r; },
                random: randomFace, resetAll: resetAll, verts: function () { return Array.prototype.slice.call(pos); },
                hair: function () { return window.__fmHair || null; }
            };
            ae('fm_ready', { V: V });
        }).catch(function (err) {
            console.error('[face-maker]', err);
            setStatus(L('err_load', 'Could not load the 3D face model. Please refresh.'));
            ae('fm_load_err', { msg: (err && err.message || '').slice(0, 80) });
        });

        window.addEventListener('beforeunload', function () { disposed = true; if (tickId) cancelAnimationFrame(tickId); });
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
})();
